import type {
  Editor,
  InputDescription,
} from "@smartsuite/automation-engine-api";
import { KeyValueCustomSelect, Paragraph } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import type React from "react";
import { useState } from "react";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useTheme } from "../../../../../../../../stores";
import type { InputCandidate } from "../../../../../../../../types/candidate";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import {
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
} from "../../../../../../../../utils/constants";
import { StyledReactSelectFieldContainer } from "./InputSelectStyles";
import { translateIfPresent } from "../../../../../../../../locales/i18n";

export const SimpleSelectEditor: React.FC<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  description?: InputDescription;
  editor?: Editor;
  isError?: boolean;
  validationError?: string;
  onClickTrash?: () => void;
  placeholder?: string;
}> = ({
  input,
  onChange,
  description,
  editor,
  isError,
  validationError,
  onClickTrash,
  placeholder,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const descEditor = description?.editor ?? editor;

  if (descEditor?.editor.oneofKind !== "select") {
    return null;
  }

  let inputValue = { oneofKind: "string" as const, string: "" };
  const options = descEditor?.editor.select.options.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  if (input.value?.values[0]?.value.oneofKind === "string") {
    inputValue = input.value?.values[0]?.value;
  }

  const selectedOption = options.find((o) => o.value === inputValue.string);
  const isSelectedOptionMissing =
    inputValue.string !== "" && selectedOption === undefined;
  const hasValidationError = Boolean(validationError);
  const hasError =
    (isError ?? false) || hasValidationError || isSelectedOptionMissing;
  const onOpendChange = (e: boolean): void => {
    setOpen(e);
  };

  const showPlaceholder = (): string => {
    if (placeholder !== undefined && placeholder !== "") {
      return translateIfPresent(`automation-form.${placeholder}`, placeholder);
    }
    if (description?.inputId === INPUT_APPLICATION_ID) {
      return t("automation-form.input-form-select-application");
    }
    if (description?.inputId === INPUT_SOLUTION_ID) {
      return t("automation-form.input-form-select-solution");
    }
    if (description?.inputId === "google-calendar-field-calendar-id") {
      return open ? t("automation-form.input-form-select-calendar") : "\u00a0";
    }
    return open ? t("automation-form.input-form-select-input") : "\u00a0";
  };

  return (
    <StyledReactSelectFieldContainer>
      {description && (
        <CaptionBlock description={description} onClickTrash={onClickTrash} />
      )}
      <KeyValueCustomSelect
        my="left top+2"
        state={hasError ? "error" : open ? "active" : "default"}
        value={selectedOption?.value}
        disabled={false}
        placeholder={showPlaceholder()}
        showClear={false}
        placeholderArrowStyle="filled-dark"
        options={options}
        className={open ? "container-is-open" : ""}
        portalStyles={getLocalCssColorVariables(theme)}
        onOpenedChange={onOpendChange}
        onChange={(value) => {
          onChange({
            ...input,
            value: {
              values: [
                {
                  value: {
                    oneofKind: "string",
                    string: String(value ?? ""),
                  },
                },
              ],
            },
          });
        }}
      />
      {hasValidationError && (
        <Paragraph color={"error"} size={"xs"}>
          {validationError}
        </Paragraph>
      )}
    </StyledReactSelectFieldContainer>
  );
};
